import io from 'socket.io-client';
import LZString from 'lz-string';
import socketActionTypes from './socketActionTypes';

/**
 * Supported socket operations
 */

/* eslint-disable */
const CONNECTED = socketActionTypes.CONNECTED;
const DISCONNECT = socketActionTypes.DISCONNECT;
const CONNECT_ERROR = socketActionTypes.CONNECT_ERROR;

export default class WebSocketController {
  socket;

  connected = false;

  forceClosed = true;

  constructor(url, connectCallback, disconnectCallback, options) {
    this.socket = io(url, {
      reconnection: true,
      reconnectionDelay: options ? options.reconnectionDelay : 500,
      reconnectionAttempts: options ? options.reconnectionAttempts : 5,
      rejectUnauthorized: false,
      transports: ['websocket'],
    });
    this.initializeListeners(connectCallback, disconnectCallback);
  }

  close() {
    this.forceClosed = true;
    this.socket.close();
  }

  decompressData = socketData => {
    if (typeof socketData === 'object') {
      const { data, isCompressed, dataType } = socketData;
      if (isCompressed) {
        const decompressData = LZString.decompressFromUTF16(data);
        if (dataType === 'object') {
          return JSON.parse(decompressData);
        }
        return decompressData;
      }
      return data;
    }
    return socketData || '';
  };

  customCallback = (error, data, callback) => {
    if (error) {
      callback && callback(error, data);
    } else {
      const deCompressedData = this.decompressData(data);
      callback && callback(error, deCompressedData);
    }
  };

  customSubscriber = (data, callback) => {
    const deCompressedData = this.decompressData(data);
    callback && callback(deCompressedData);
  };

  initializeListeners(connectCallback, disconnectCallback) {
    this.socket.on(CONNECTED, this.onConnected);
    this.socket.on(CONNECTED, connectCallback);
    this.socket.on(DISCONNECT, this.onDisconnected);
    if (disconnectCallback) {
      this.socket.on(DISCONNECT, reason => {
        if (this.forceClosed) {
          disconnectCallback(reason);
        }
      });
    }
    if (disconnectCallback) {
      this.socket.on(CONNECT_ERROR, disconnectCallback);
    }
    this.socket.on(CONNECT_ERROR, this.onConnectionError);
  }

  onConnected = () => {
    this.connected = true;
  };

  onDisconnected = (...args) => {
    this.connected = false;
  };

  onConnectionError = error => {
    this.connected = false;
  };

  sendMessageUncompressed = (event, ...args) => {
    if (this.socket) {
      this.socket.emit(event, ...args);
    }
  };

  sendMessage = (event, ...args) => {
    if (this.socket && this.connected) {
      this.socket.emit(event, ...args.slice(0, args.length - 1), (...response) => this.customCallback(...response, args[args.length - 1]),
      );
    } else {
      args[args.length - 1] &&
        args[args.length - 1]({
          errorCode: 'socket_disconnected',
        });
    }
  };

  redisSubscribe = (event, storeId, callback, subscriber) => {
    this.sendMessage(event, storeId, (error, response) => {
      callback(error, response);
      if (!error) {
        const { id } = response;
        this.socket.on(id, (...args) => {
          this.customSubscriber(...args, subscriber);
        });
      }
    });
  };

  subscribe = (event, storeId, callback, subscriber) => {
    if (this.socket) {
      this.socket.emit(event, storeId, (...args) => this.customCallback(...args, callback));
      this.socket.on(`${event}`, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  subscribeEmployeeId = (event, storeId, employeeId, callback, subscriber) => {
    if (this.socket) {
      this.socket.emit(event, storeId, employeeId, (...args) => this.customCallback(...args, callback),
      );
      this.socket.on(event, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  subscribeExtraData = (event, storeId, extraData, callback, subscriber) => {
    if (this.socket) {
      this.socket.emit(event, storeId, extraData, (...args) => this.customCallback(...args, callback),
      );
      this.socket.on(event, (...args) => this.customSubscriber(...args, subscriber));
    }
  };

  unsubscribe = event => {
    this.socket.off(event);
  };

  disconnect = () => {
    this.socket.disconnect();
  }
}
